import R from 'ramda';
import Cookies from "js-cookie";
import { getBreakpoints } from '../../components/BreakpointProvider/breakpointHelper';
import getPathSegments from '../../helpers/get-path-segments';

export const getPlatform = ({ window, sassBreakpoints }) => {
  const hasTouch = 'ontouchstart' in window;
  const breakpoints = getBreakpoints(sassBreakpoints);

  const name = R.compose(
    R.propOr('', 'name'),
    R.defaultTo({}),
    R.find(({ mediaQueryString }) => window.matchMedia(mediaQueryString).matches),
  )(breakpoints);

  if (name.includes('small') && hasTouch) {
    return 'mobile';
  }

  if (name === 'medium' && hasTouch) {
    return 'tablet';
  }

  return 'web';
};

export const getReferrer = ({ referringURL }) => {
  try {
    const { hostname } = new URL(referringURL);
    return hostname;
  } catch (err) {
    return '';
  }
};

export const getCountry = ({ locale }) => (
  R.compose(
    R.toUpper,
    R.last,
    R.split('-'),
  )(locale)
);

export const getLanguage = ({ locale }) => (
  R.compose(
    R.propOr('', 0),
    R.split('-'),
  )(locale)
);

export const getPageHierarchy = ({
  pathname,
  country,
  language,
  locale,
}) => {
  const pageHierarchy = R.compose(
    R.join('|'),
    R.slice(1, Infinity),
    R.split('/'),
    R.replace(`/${locale}`, ''),
  )(pathname);

  return `${country}|${language}|${pageHierarchy}`;
};

const getDigitialData = ({
  window,
  serverTrackingData = {},
  ENVIRONMENT = 'staging',
}) => {
  const sassBreakpoints = R.prop('breakpoints', serverTrackingData);
  const pageURL = R.pathOr('', ['location', 'href'], window);
  const domain = R.pathOr('', ['location', 'host'], window);
  const pathname = R.pathOr('', ['location', 'pathname'], window);
  let locale = R.propOr('', 'locale', serverTrackingData);
  let primaryCategory = '';
  let subCategory1 = '';
  let subCategory2 = '';

  if (locale) {
    [
      primaryCategory,
      subCategory1,
      subCategory2,
    ] = getPathSegments({ path: pathname, takeLast: false });
  } else {
    [
      locale,
      primaryCategory,
      subCategory1,
      subCategory2,
    ] = getPathSegments({ path: pathname, takeLast: false });
  }

  const pageName = R.replace(`/${locale}`, '', pathname);
  const pageTitle = R.pathOr('', ['document', 'title'], window);
  const referringURL = R.pathOr('', ['document', 'referrer'], window);
  const referrer = getReferrer({ referringURL });
  const country = getCountry({ locale });
  const language = getLanguage({ locale });
  const platform = getPlatform({ window, sassBreakpoints });
  const pageInstanceId = `${domain} - ${ENVIRONMENT}`;
  const pageHierarchy = getPageHierarchy({
    pathname,
    country,
    language,
    locale,
  });

  const {
    version = '',
    pageId = '',
    pageType = '',
    breadcrumbs = '',
    currencyCode = '',
    errorPage = '',
    search = '',
    contentData = '',
    product = '',
  } = serverTrackingData;

  function getVisitorType() {
    const snr = Cookies.get('s_nr');
    if (snr) {
      const parts = snr.split('-');
      return parts[1] ? parts[1].toLowerCase() : 'not available';
    }
    return 'not available';
  }

  return {
    version: {
      platform: 'ccx',
      version,
    },
    pageInstanceId,
    user: {
      userCountry: Cookies.get('country') || '',
      visitorType: getVisitorType(),
      status: 'anonymous',
    },
    page: {
      pageInfo: {
        pageId,
        pageName,
        pageURL,
        referrer,
        referringURL,
        pageTitle,
      },
      category: {
        pageType,
        primaryCategory,
        subCategory1,
        subCategory2,
      },
      breadcrumbs,
      attributes: {
        country,
        language,
        platform,
        domain,
        currencyCode,
        pageHierarchy,
        errorPage,
      },
    },
    ...search && { search },
    ...contentData && { contentData },
    ...product && { product },
  };
};

export default getDigitialData;
